import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Box,
  Menu,
  Tooltip,
  MenuItem,
  Divider,
  Paper,
  TextField,
  InputAdornment,
  Popper,
} from "@mui/material";
import { BringToFrontIcon } from "../../../../icons";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import {
  setContentLayers,
  setTextLayers,
} from "../../../../store/actions/cmsAction";
import { TypographySmall } from "../../../customComponent";
import { colorStyling } from "../../../../helpers/color";
import { useDebounce } from "../../../hooks";
import FONTS from "../../../../fonts";
import update from "immutability-helper";
import {
  fullColorToHex,
  inputOnlyNumbers,
} from "../../../../helpers/inputProcessing";
import { ChromePicker } from "react-color";

export const CMenuButton = styled(Button)({
  fontSize: 14,
  fontWeight: 600,
  padding: "4px",
  minWidth: 50,
  height: 30,
  color: "black",
  cursor: "default",
  backgroundColor: grey[100],
  "&:hover": {
    backgroundColor: grey[300],
  },
  "&.disabled-btn": {
    color: grey[400],
  },
});

const TextLayerButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(grey[200]),
  backgroundColor: grey[200],
  "&:hover": {
    backgroundColor: grey[500],
  },
}));

const ContextMenu = ({
  open,
  menu,
  layer,
  layerID,
  layerItem,
  handleClose,
  handleRemoveLayer,
  itemIndex,
  setItemIndex,
  shownAsset,
  setItemChildrenIndex,
  isLayerLocked,
  setIsLayerLocked,
}) => {
  const dispatch = useDispatch();
  const { contentLayers, layerMedias, textOptionsOpen } = useSelector(
    (state) => state.cms
  );

  const { t } = useTranslation("cms");

  const dummyRef = useRef(null);

  const [bgColor, setBgColor] = useState("");
  const [textFamily, setTextFamily] = useState("ARIAL");
  const [textSize, setTextSize] = useState("");
  const [textWeight, setTextWeight] = useState("");
  const [textColor, setTextColor] = useState("");
  const [textHAlign, setTextHAlign] = useState("LEFT");
  const [textVAlign, setTextVAlign] = useState("CENTER");
  const [textBold, setTextBold] = useState(false);

  const [anchorElBg, setAnchorElBg] = useState(null);
  const openColorPickerBg = Boolean(anchorElBg);
  const handleOpenColorBg = (event) => {
    setAnchorElText(null);
    setAnchorElHAlign(null);
    setAnchorElVAlign(null);
    if (openColorPickerBg) {
      setAnchorElBg(null);
      return;
    }
    setAnchorElBg(event.currentTarget);
  };
  const handleCloseColorBg = (event) => {
    setAnchorElBg(null);
  };

  const [anchorElText, setAnchorElText] = useState(null);
  const openColorPickerText = Boolean(anchorElText);
  const handleOpenColorText = (event) => {
    setAnchorElBg(null);
    setAnchorElHAlign(null);
    setAnchorElVAlign(null);
    if (openColorPickerText) {
      setAnchorElText(null);
      return;
    }
    setAnchorElText(event.currentTarget);
  };
  const handleCloseColorText = (event) => {
    setAnchorElText(null);
  };

  const [anchorElHAlign, setAnchorElHAlign] = useState(null);
  const openMenuHAlign = Boolean(anchorElHAlign);
  const handleOpenMenuHAlign = (event) => {
    setAnchorElBg(null);
    setAnchorElText(null);
    setAnchorElVAlign(null);
    if (openMenuHAlign) {
      setAnchorElHAlign(null);
      return;
    }
    setAnchorElHAlign(event.currentTarget);
  };
  const handleCloseMenuHAlign = (event) => {
    setAnchorElHAlign(null);
  };

  const [anchorElVAlign, setAnchorElVAlign] = useState(null);
  const openMenuVAlign = Boolean(anchorElVAlign);
  const handleOpenMenuVAlign = (event) => {
    setAnchorElBg(null);
    setAnchorElText(null);
    setAnchorElHAlign(null);
    if (openMenuVAlign) {
      setAnchorElVAlign(null);
      return;
    }
    setAnchorElVAlign(event.currentTarget);
  };
  const handleCloseMenuVAlign = (event) => {
    setAnchorElVAlign(null);
  };

  const textLayer = useMemo(() => {
    return layer.text;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer]);

  useEffect(() => {
    if (textOptionsOpen) {
      document.body.style.overflowX = "hidden";
      if (layer.type === "text") {
        setBgColor(layer.bgColor);
        setTextFamily(textLayer.family ?? "ARIAL");
        setTextSize(textLayer.size);
        setTextWeight(textLayer.weight);
        setTextColor(textLayer.color);
        setTextHAlign(textLayer.hAlign);
        setTextVAlign(textLayer.vAlign);
        const isBold = textLayer.weight < 500 ? false : true;
        setTextBold(isBold);
      }
    } else {
      // Restore original overflow state when Popper is closed
      document.body.style.overflowX = "";
      setAnchorElBg(null);
      setAnchorElText(null);
      setAnchorElHAlign(null);
      setAnchorElVAlign(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textOptionsOpen, textLayer]);

  const setBoxToActive = useCallback(
    (id, layers) => {
      const targetLayers = layers ?? contentLayers;
      let mergedUpdates = { ...targetLayers };
      const layerKeys = Object.keys(mergedUpdates);
      for (let i = 0; i < layerKeys.length; i++) {
        const layerID = layerKeys[i];
        if (layerID === id) {
          mergedUpdates = update(mergedUpdates, {
            [layerID]: { $merge: { active: true } },
          });
        } else {
          mergedUpdates = update(mergedUpdates, {
            [layerID]: { $merge: { active: false } },
          });
        }
      }

      const finalUpdates = update(targetLayers, { $merge: mergedUpdates });
      if (layers) {
        return finalUpdates;
      } else {
        dispatch(setContentLayers({ ...finalUpdates }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const bringLayerToFront = useCallback(
    (id) => {
      const copy = { ...contentLayers };
      const layerKeys = Object.keys(copy);
      const lastIndex = layerKeys.length;
      const fromIndex = copy[id].dimensions.zIndex;
      layerKeys.forEach((layerID) => {
        const currentZIndex = copy[layerID].dimensions.zIndex;
        if (currentZIndex > fromIndex) {
          copy[layerID] = update(copy[layerID], {
            dimensions: { $merge: { zIndex: currentZIndex - 1 } },
          });
        }
      });

      copy[id] = update(copy[id], {
        dimensions: { $merge: { zIndex: lastIndex } },
      });

      let updated = update(contentLayers, { $set: copy });
      updated = setBoxToActive(id, updated);
      dispatch(setContentLayers({ ...updated }));
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const sendLayerToBack = useCallback(
    (id) => {
      const copy = { ...contentLayers };
      const layerKeys = Object.keys(copy);
      const firstIndex = 1; // Assuming zIndex starts at 1
      const fromIndex = copy[id].dimensions.zIndex;

      // Shift up the zIndex of all layers below the target layer
      layerKeys.forEach((layerID) => {
        const currentZIndex = copy[layerID].dimensions.zIndex;
        if (currentZIndex < fromIndex) {
          copy[layerID] = update(copy[layerID], {
            dimensions: { $merge: { zIndex: currentZIndex + 1 } },
          });
        }
      });

      // Set the target layer's zIndex to the lowest value
      copy[id] = update(copy[id], {
        dimensions: { $merge: { zIndex: firstIndex } },
      });

      let updated = update(contentLayers, { $set: copy });
      updated = setBoxToActive(id, updated);
      dispatch(setContentLayers({ ...updated }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const handleMenuSlidePrev = () => {
    handleClose();
    if (shownAsset.children) {
      setItemChildrenIndex((value) => {
        let updatedIndex = 0 + value[shownAsset.id];
        updatedIndex -= 1;
        if (updatedIndex < 0) {
          updatedIndex = shownAsset.children.length - 1;
        }

        let updated = { ...value };
        updated[shownAsset.id] = updatedIndex;
        return updated;
      });
    }

    let value = itemIndex - 1;
    if (value < 0) {
      if (layerMedias[layerID]?.length) {
        value = layerMedias[layerID]?.length - 1;
      } else {
        value = 0;
      }
    }
    setItemIndex(value);
  };

  const handleMenuSlideNext = () => {
    handleClose();
    if (shownAsset.children) {
      setItemChildrenIndex((value) => {
        let updatedIndex = 0 + value[shownAsset.id];
        updatedIndex += 1;
        if (updatedIndex >= shownAsset.children.length) {
          updatedIndex = 0;
        }

        let updated = { ...value };
        updated[shownAsset.id] = updatedIndex;
        return updated;
      });
    }

    let value = itemIndex + 1;
    const assetLength = layerMedias[layerID]?.length;
    if (assetLength) {
      if (value >= assetLength) {
        value = 0;
      }
    } else {
      value = 0;
    }
    setItemIndex(value);
  };

  const handleMenuBringToFront = () => {
    bringLayerToFront(layerID);
    handleClose();
  };

  const handleMenuSendToBack = () => {
    sendLayerToBack(layerID);
    handleClose();
  };

  const handleMenuLockLayer = () => {
    if (isLayerLocked) {
      setBoxToActive(layerID);
    }

    const switchValue = !isLayerLocked;
    const copy = { ...contentLayers };
    copy[layerID] = update(copy[layerID], {
      dimensions: { $merge: { lockRatio: switchValue } },
    });
    let updated = update(contentLayers, { $set: copy });
    dispatch(setContentLayers({ ...updated }));

    setIsLayerLocked(switchValue);
    handleClose();
  };

  const handleChangeTextInput = (event, which, fixedValue) => {
    let value = event?.target?.value;

    let updated;
    switch (which) {
      case "family":
        setTextFamily(value);
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { family: value } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      case "size":
        if (!event) value = parseInt(textSize) + fixedValue;
        if (!value || parseInt(value) < 1) {
          value = "1";
        }
        if (parseInt(value) > 1000) {
          value = "1000";
        }
        setTextSize(value.toString());
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      case "weight":
        let updatedWeight;
        if (!textBold) {
          updatedWeight = 700;
          setTextBold(true);
        } else {
          updatedWeight = 400;
          setTextBold(false);
        }
        setTextWeight(updatedWeight.toString());
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      case "hAlign":
        setTextHAlign(fixedValue);
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { hAlign: fixedValue } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        handleCloseMenuHAlign();
        break;

      case "vAlign":
        setTextVAlign(fixedValue);
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { vAlign: fixedValue } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        handleCloseMenuVAlign();
        break;

      case "scrolling":
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { scrolling: fixedValue } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      case "direction":
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { direction: value } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      case "speed":
        if (!value || parseInt(value) < 1) {
          value = "1";
        }
        if (parseInt(value) > 10) {
          value = "10";
        }
        updated = update(contentLayers, {
          [layerID]: { text: { $merge: { speed: value } } },
        });
        dispatch(setContentLayers({ ...updated }));
        dispatch(setTextLayers({ edit: { id: layerID, value: true } }));
        break;

      default:
        break;
    }
  };

  const debouncedSize = useDebounce(textSize, 300);
  useEffect(
    () => {
      if (layer.type === "text" && debouncedSize.length > 0) {
        const value = debouncedSize;
        const updated = update(contentLayers, {
          [layerID]: { text: { $merge: { size: value } } },
        });
        dispatch(setContentLayers({ ...updated }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSize] // Only call effect if debounced interval changes
  );

  const debouncedWeight = useDebounce(textWeight, 300);
  useEffect(
    () => {
      if (layer.type === "text" && debouncedWeight.length > 0) {
        const value = debouncedWeight;
        const updated = update(contentLayers, {
          [layerID]: { text: { $merge: { weight: value } } },
        });
        dispatch(setContentLayers({ ...updated }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedWeight] // Only call effect if debounced interval changes
  );

  const handleChangeColor = useCallback(
    (which, color) => {
      const value = fullColorToHex(color);

      let updated = null;
      switch (which) {
        case "bg":
          setBgColor(value);
          updated = update(contentLayers, {
            [layerID]: { bgColor: { $set: value } },
          });
          break;

        case "text":
        default:
          setTextColor(value);
          updated = update(contentLayers, {
            [layerID]: { text: { $merge: { color: value } } },
          });
          break;
      }
      dispatch(setContentLayers({ ...updated }));
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const parseHAlignIcon = useMemo(() => {
    switch (textHAlign) {
      default:
      case "LEFT":
        return <FormatAlignLeftIcon fontSize="small" />;
      case "CENTER":
        return <FormatAlignCenterIcon fontSize="small" />;
      case "RIGHT":
        return <FormatAlignRightIcon fontSize="small" />;
      case "JUSTIFY":
        return <FormatAlignJustifyIcon fontSize="small" />;
    }
  }, [textHAlign]);

  const parseVAlignIcon = useMemo(() => {
    switch (textVAlign) {
      default:
      case "TOP":
        return <VerticalAlignTopIcon fontSize="small" />;
      case "CENTER":
        return <VerticalAlignCenterIcon fontSize="small" />;
      case "BOTTOM":
        return <VerticalAlignBottomIcon fontSize="small" />;
    }
  }, [textVAlign]);

  const disableSlideMenuBtn = useMemo(() => {
    if (!layerMedias[layerID] || layerMedias[layerID]?.length < 1) {
      return true;
    }
    if (layerMedias[layerID]?.length === 1) {
      const firstMedia = layerMedias[layerID][0];
      if (firstMedia.children?.length > 1) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }, [layerMedias, layerID]);

  return (
    <>
      <Menu
        ref={dummyRef}
        open={open}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          open ? { top: menu.mainY, left: menu.mainX } : undefined
        }
        slotProps={{ root: { sx: { zIndex: 1000 } } }}
        MenuListProps={{ sx: { p: "4px" } }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"start"}
        >
          <Tooltip
            placement="top"
            disableInteractive
            title={t("cmenu.slidePreviousTip")}
          >
            <CMenuButton
              variant="contained"
              className={disableSlideMenuBtn ? "disabled-btn" : ""}
              onClick={disableSlideMenuBtn ? null : handleMenuSlidePrev}
            >
              <ArrowBackIcon />
            </CMenuButton>
          </Tooltip>

          <Tooltip
            placement="top"
            disableInteractive
            title={t("cmenu.slideNextTip")}
          >
            <CMenuButton
              variant="contained"
              className={disableSlideMenuBtn ? "disabled-btn" : ""}
              onClick={disableSlideMenuBtn ? null : handleMenuSlideNext}
            >
              <ArrowForwardIcon />
            </CMenuButton>
          </Tooltip>

          <Tooltip
            placement="top"
            disableInteractive
            title={t("cmenu.removeLayerTip")}
          >
            <CMenuButton
              variant="contained"
              onClick={handleRemoveLayer}
              sx={{
                color: colorStyling.cms.red,
              }}
            >
              <DeleteIcon />
            </CMenuButton>
          </Tooltip>
        </Box>

        <Divider sx={{ py: "2px" }} />

        <MenuItem onClick={handleMenuBringToFront} sx={{ cursor: "default " }}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ width: "100%", height: 25 }}
          >
            <BringToFrontIcon />
            <TypographySmall
              sx={{
                ml: "4px",
                color: "black",
                textAlign: "center",
              }}
            >
              {t("cmenu.btFrontlabel")}
            </TypographySmall>
          </Box>
        </MenuItem>

        <MenuItem onClick={handleMenuSendToBack} sx={{ cursor: "default " }}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ width: "100%", height: 25 }}
          >
            <BringToFrontIcon />
            <TypographySmall
              sx={{
                ml: "4px",
                color: "black",
                textAlign: "center",
              }}
            >
              {t("cmenu.stBacklabel")}
            </TypographySmall>
          </Box>
        </MenuItem>

        <MenuItem onClick={handleMenuLockLayer} sx={{ cursor: "default " }}>
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ width: "100%", height: 25 }}
          >
            {!isLayerLocked ? <LockIcon /> : <LockOpenIcon />}
            <TypographySmall
              sx={{
                ml: "4px",
                color: "black",
                textAlign: "center",
              }}
            >
              {!isLayerLocked
                ? t("cmenu.lockLayerLabel")
                : t("cmenu.unlockLayerLabel")}
            </TypographySmall>
          </Box>
        </MenuItem>
      </Menu>

      {layer.type === "text" && (
        <>
          <Popper
            open={textOptionsOpen && layer.active}
            anchorEl={layerItem}
            placement="top"
            slotProps={{
              root: {
                style: { zIndex: 1000 },
              },
            }}
          >
            {(openColorPickerText ||
              openColorPickerBg ||
              openMenuHAlign ||
              openMenuVAlign) && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  zIndex: 1001,
                }}
                onClick={() => {
                  handleCloseColorText();
                  handleCloseColorBg();
                  handleCloseMenuHAlign();
                  handleCloseMenuVAlign();
                }}
              ></Box>
            )}

            <Paper
              onClick={(event) => {
                event.stopPropagation();
              }}
              elevation={1}
              sx={{
                zIndex: 1000,
              }}
            >
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                }}
                sx={{ width: "100%", paddingX: 1 }}
                display={"flex"}
                alignItems={"center"}
              >
                <Box
                  display={"flex"}
                  justifyContent={"left"}
                  alignItems={"center"}
                >
                  <Box display={"flex"} alignItems={"center"}>
                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontFamilyTip")}
                    >
                      <TextField
                        margin="dense"
                        variant="outlined"
                        size="small"
                        select
                        SelectProps={{
                          native: true,
                          fullWidth: true,
                        }}
                        defaultValue={textFamily}
                        onChange={(e) => {
                          handleChangeTextInput(e, "family");
                        }}
                        sx={{ ml: 1, width: "163px" }}
                      >
                        {Object.keys(FONTS).map((font) => (
                          <option value={font} key={font}>
                            {FONTS[font].label}
                          </option>
                        ))}
                      </TextField>
                    </Tooltip>
                  </Box>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    sx={{ marginX: "4px" }}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontSizeDecTip")}
                    >
                      <TextLayerButton
                        variant="contained"
                        onClick={() => handleChangeTextInput(null, "size", -1)}
                        sx={{
                          padding: 0,
                          marginRight: "4px",
                          minWidth: 25,
                          height: 25,
                          fontWeight: 700,
                        }}
                      >
                        {" – "}
                      </TextLayerButton>
                    </Tooltip>

                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontSizeTip")}
                    >
                      <TextField
                        margin="dense"
                        variant="outlined"
                        placeholder={t("designer.fontSizePlaceholder")}
                        size="small"
                        value={textSize}
                        onChange={(e) => handleChangeTextInput(e, "size")}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        sx={{ width: "80px" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">px</InputAdornment>
                          ),
                        }}
                      />
                    </Tooltip>

                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontSizeIncTip")}
                    >
                      <TextLayerButton
                        variant="contained"
                        onClick={() => handleChangeTextInput(null, "size", +1)}
                        sx={{
                          padding: 0,
                          marginLeft: "4px",
                          minWidth: 25,
                          height: 25,
                          color: "black",
                          fontWeight: 700,
                        }}
                      >
                        {" + "}
                      </TextLayerButton>
                    </Tooltip>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    sx={{ marginX: "4px" }}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontBoldTip")}
                    >
                      <Button
                        variant="contained"
                        onClick={() => handleChangeTextInput(null, "weight")}
                        sx={{
                          padding: "4px",
                          minWidth: 36,
                          height: 33,
                          color: "black",
                          backgroundColor: textBold ? grey[500] : grey[300],
                          "&:hover": {
                            color: textBold ? "white" : "black",
                            backgroundColor: textBold ? grey[700] : grey[500],
                          },
                        }}
                      >
                        <FormatBoldIcon />
                      </Button>
                    </Tooltip>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    sx={{ marginX: "4px" }}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={t("designer.fontColorTip")}
                    >
                      <TextLayerButton
                        variant="contained"
                        onClick={handleOpenColorText}
                        sx={{
                          padding: "4px",
                          minWidth: 36,
                          height: 33,
                          fontWeight: 700,
                        }}
                      >
                        <FormatColorTextIcon sx={{ color: textLayer?.color }} />
                      </TextLayerButton>
                    </Tooltip>
                    <Popper
                      open={openColorPickerText}
                      anchorEl={anchorElText}
                      placement="bottom-start"
                      style={{ zIndex: 1001 }}
                    >
                      <Box sx={{ marginY: "4px" }}>
                        <ChromePicker
                          color={textColor}
                          onChange={(color) =>
                            setTextColor(fullColorToHex(color))
                          }
                          onChangeComplete={(color) =>
                            handleChangeColor(`text`, color)
                          }
                          styles={{ "margin-top": 1 }}
                        />
                      </Box>
                    </Popper>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    sx={{ marginX: "4px" }}
                  >
                    <TextLayerButton
                      variant="contained"
                      onClick={handleOpenColorBg}
                      sx={{
                        padding: "4px",
                        minWidth: 36,
                        height: 33,
                        fontWeight: 700,
                      }}
                    >
                      <FormatColorFillIcon sx={{ color: layer.bgColor }} />
                    </TextLayerButton>
                    <Popper
                      open={openColorPickerBg}
                      anchorEl={anchorElBg}
                      placement="bottom-start"
                      style={{ zIndex: 1001 }}
                    >
                      <Box sx={{ marginY: "4px" }}>
                        <ChromePicker
                          color={bgColor}
                          onChange={(color) =>
                            setBgColor(fullColorToHex(color))
                          }
                          onChangeComplete={(color) =>
                            handleChangeColor(`bg`, color)
                          }
                          styles={{ "margin-top": 1 }}
                        />
                      </Box>
                    </Popper>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    sx={{ marginX: "4px" }}
                  >
                    <Box>
                      <Tooltip
                        arrow
                        disableInteractive
                        title={
                          openMenuHAlign ? "" : t("designer.fontHAlignTip")
                        }
                      >
                        <TextLayerButton
                          variant="contained"
                          onClick={handleOpenMenuHAlign}
                          sx={{
                            padding: "4px",
                            marginRight: "4px",
                            minWidth: 36,
                            height: 33,
                            color: "black",
                            fontWeight: 700,
                          }}
                        >
                          {parseHAlignIcon}
                        </TextLayerButton>
                      </Tooltip>
                      <Popper
                        open={openMenuHAlign}
                        anchorEl={anchorElHAlign}
                        placement="bottom"
                        style={{ zIndex: 1001, backgroundColor: "white" }}
                      >
                        <Paper display={"flex"} sx={{ mt: 1, mb: "4px" }}>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontHAlignLeft")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "hAlign", "LEFT")
                              }
                              sx={{
                                color: textHAlign === "LEFT" ? "blue" : "black",
                                padding: "4px",
                                marginRight: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <FormatAlignLeftIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontHAlignCenter")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "hAlign", "CENTER")
                              }
                              sx={{
                                color:
                                  textHAlign === "CENTER" ? "blue" : "black",
                                padding: "4px",
                                marginRight: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <FormatAlignCenterIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontHAlignRight")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "hAlign", "RIGHT")
                              }
                              sx={{
                                color:
                                  textHAlign === "RIGHT" ? "blue" : "black",
                                padding: "4px",
                                marginRight: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <FormatAlignRightIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontHAlignJustify")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "hAlign", "JUSTIFY")
                              }
                              sx={{
                                color:
                                  textHAlign === "JUSTIFY" ? "blue" : "black",
                                padding: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <FormatAlignJustifyIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Paper>
                      </Popper>
                    </Box>

                    <Box>
                      <Tooltip
                        arrow
                        disableInteractive
                        title={
                          openMenuVAlign ? "" : t("designer.fontVAlignTip")
                        }
                      >
                        <TextLayerButton
                          variant="contained"
                          onClick={handleOpenMenuVAlign}
                          sx={{
                            padding: "4px",
                            marginRight: "4px",
                            minWidth: 36,
                            height: 33,
                            color: "black",
                            fontWeight: 700,
                          }}
                        >
                          {parseVAlignIcon}
                        </TextLayerButton>
                      </Tooltip>
                      <Popper
                        open={openMenuVAlign}
                        anchorEl={anchorElVAlign}
                        placement="bottom"
                        style={{ zIndex: 1001, backgroundColor: "white" }}
                      >
                        <Paper display={"flex"} sx={{ mt: 1, mb: "4px" }}>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontVAlignTop")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "vAlign", "TOP")
                              }
                              sx={{
                                color: textVAlign === "TOP" ? "blue" : "black",
                                padding: "4px",
                                marginRight: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <VerticalAlignTopIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontVAlignCenter")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "vAlign", "CENTER")
                              }
                              sx={{
                                color:
                                  textVAlign === "CENTER" ? "blue" : "black",
                                padding: "4px",
                                marginRight: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <VerticalAlignCenterIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip
                            arrow
                            disableInteractive
                            title={t("designer.fontVAlignBottom")}
                          >
                            <Button
                              onClick={() =>
                                handleChangeTextInput(null, "vAlign", "BOTTOM")
                              }
                              sx={{
                                color:
                                  textVAlign === "BOTTOM" ? "blue" : "black",
                                padding: "4px",
                                minWidth: 36,
                                height: 33,
                              }}
                            >
                              <VerticalAlignBottomIcon fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Paper>
                      </Popper>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Popper>
        </>
      )}
    </>
  );
};

export default ContextMenu;
